import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Send, X, MessageSquare, ThumbsUp, ThumbsDown } from 'lucide-react';
type TabContext = 'risk_matrix' | 'recommendations' | 'analytics';
interface Message {
  id: string;
  role: 'user' | 'ai';
  content: string;
  timestamp: Date;
  feedback?: 'up' | 'down';
}
interface BiltiChatPanelProps {
  tabContext: TabContext;
}
const TAB_CONFIGS = {
  risk_matrix: {
    greeting: "Hi! I'm Bilti. I can help you analyze the Risk Matrix. What would you like to know?",
    prompts: ['Compare the highest risk projects', 'Explain the top risk drivers', 'How do I tune the risk weights?', 'Which parameters need attention?']
  },
  recommendations: {
    greeting: "Hi! I'm Bilti. I can help you review safety insights and recommendations. How can I assist?",
    prompts: ['What are the top priority recommendations?', 'How do I implement the SIF prevention steps?', 'Show me the best risk reduction strategies', 'Which regions need the most help?']
  },
  analytics: {
    greeting: "Hi! I'm Bilti. I can help you analyze safety trends and correlations. What should we look at?",
    prompts: ['What are the most concerning trend patterns?', 'Compare performance across regions', 'Are there any anomalies in the data?', 'Explain the correlation between weather and incidents']
  }
};
export function BiltiChatPanel({
  tabContext
}: BiltiChatPanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Reset messages when tab context changes
  useEffect(() => {
    setMessages([{
      id: 'greeting',
      role: 'ai',
      content: TAB_CONFIGS[tabContext].greeting,
      timestamp: new Date()
    }]);
  }, [tabContext]);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);
  const generateMockResponse = (query: string, context: TabContext): string => {
    const lowerQuery = query.toLowerCase();
    if (context === 'risk_matrix') {
      if (lowerQuery.includes('compare') || lowerQuery.includes('highest')) {
        return 'Based on the Risk Matrix, Hudson Yards and Court Square LIC currently show the highest overall risk scores. Hudson Yards is driven primarily by fatigue risk, while Court Square is seeing spikes in observation severity.';
      }
      if (lowerQuery.includes('driver') || lowerQuery.includes('parameter')) {
        return 'The primary risk drivers across your portfolio right now are Worker Fatigue (Workforce & Contractors category) and PTP Quality (Safety Planning & Controls). I recommend focusing on these areas first.';
      }
      if (lowerQuery.includes('tune') || lowerQuery.includes('weight')) {
        return "You can adjust risk weights using the 'Tune' panel on the right side of the Risk Matrix. Changes there will immediately recalculate the scores across all projects.";
      }
    } else if (context === 'recommendations') {
      if (lowerQuery.includes('priority') || lowerQuery.includes('top')) {
        return "I recommend prioritizing the 'Standardize PTP Review Process' recommendation. It addresses a systemic issue across 4 projects and has the highest potential risk reduction (12 pts).";
      }
      if (lowerQuery.includes('implement') || lowerQuery.includes('strategy')) {
        return "To implement these strategies effectively, start by assigning an owner to the action items in the 'Take Action' dropdown. I can help draft a communication plan for the site teams if you'd like.";
      }
    } else if (context === 'analytics') {
      if (lowerQuery.includes('trend') || lowerQuery.includes('pattern')) {
        return "Looking at the 90-day trends, there's a concerning divergence in the Northeast region where PTP scores are declining while open observations are rising. This pattern often precedes an increase in incident rates.";
      }
      if (lowerQuery.includes('compare') || lowerQuery.includes('region')) {
        return "The Midwest region is currently outperforming the West Coast by 14 points in overall risk score. The Midwest's improvement is largely driven by consistent PTP adoption and lower crew turnover.";
      }
      if (lowerQuery.includes('weather') || lowerQuery.includes('correlation')) {
        return 'There is a strong correlation in the Southeast region: when weather severity scores exceed 80, open incident counts typically spike within 5-7 days. This is a highly predictable pattern.';
      }
    }
    // Generic fallback
    return `That's a great question about the ${context.replace('_', ' ')}. Based on the current data, I'm seeing some interesting patterns. Would you like me to break down specific risk factors or compare specific projects?`;
  };
  const handleSend = (text: string) => {
    if (!text.trim()) return;
    const newUserMsg: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: text,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, newUserMsg]);
    setInputValue('');
    setIsTyping(true);
    // Simulate AI response delay
    setTimeout(() => {
      const aiResponse: Message = {
        id: (Date.now() + 1).toString(),
        role: 'ai',
        content: generateMockResponse(text, tabContext),
        timestamp: new Date()
      };
      setMessages(prev => [...prev, aiResponse]);
      setIsTyping(false);
    }, 1500);
  };
  const handleFeedback = (messageId: string, type: 'up' | 'down') => {
    setMessages(prev => prev.map(msg => msg.id === messageId ? {
      ...msg,
      feedback: type
    } : msg));
  };
  return <div className="fixed bottom-6 right-6 z-[40] flex flex-col items-end" data-id="element-1247">
      <AnimatePresence data-id="element-1248">
        {isOpen && <motion.div initial={{
        opacity: 0,
        scale: 0.9,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.9,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="mb-4 w-[380px] h-[500px] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden" data-id="element-1249">
            {/* Header */}
            <div className="bg-gradient-to-r from-slate-900 to-indigo-900 p-4 flex items-center justify-between shrink-0" data-id="element-1250">
              <div className="flex items-center gap-3" data-id="element-1251">
                <div className="w-8 h-8 rounded-full bg-amber-400 flex items-center justify-center shadow-inner" data-id="element-1252">
                  <Sparkles size={16} className="text-slate-900" data-id="element-1253" />
                </div>
                <div data-id="element-1254">
                  <h3 className="text-white font-semibold text-sm" data-id="element-1255">
                    Discuss with Bilti
                  </h3>
                  <p className="text-indigo-200 text-xs" data-id="element-1256">AI Safety Advisor</p>
                </div>
              </div>
              <button onClick={() => setIsOpen(false)} className="text-indigo-200 hover:text-white transition-colors p-1 rounded-md hover:bg-white/10" data-id="element-1257">
                <X size={18} data-id="element-1258" />
              </button>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 bg-gray-50 flex flex-col gap-4" data-id="element-1259">
              {messages.map(msg => <div key={msg.id} className={`flex flex-col max-w-[85%] ${msg.role === 'user' ? 'self-end' : 'self-start'}`} data-id="element-1260">
                  <div className={`p-3 rounded-2xl text-sm shadow-sm ${msg.role === 'user' ? 'bg-indigo-600 text-white rounded-tr-sm' : 'bg-white border border-gray-200 text-gray-800 rounded-tl-sm'}`} data-id="element-1261">
                    {msg.content}
                  </div>

                  {msg.role === 'ai' && msg.id !== 'greeting' && <div className="flex items-center gap-2 mt-1 ml-1" data-id="element-1262">
                      <button onClick={() => handleFeedback(msg.id, 'up')} className={`p-1 rounded hover:bg-gray-200 transition-colors ${msg.feedback === 'up' ? 'text-green-600' : 'text-gray-400'}`} data-id="element-1263">
                        <ThumbsUp size={12} data-id="element-1264" />
                      </button>
                      <button onClick={() => handleFeedback(msg.id, 'down')} className={`p-1 rounded hover:bg-gray-200 transition-colors ${msg.feedback === 'down' ? 'text-red-600' : 'text-gray-400'}`} data-id="element-1265">
                        <ThumbsDown size={12} data-id="element-1266" />
                      </button>
                    </div>}
                </div>)}

              {isTyping && <div className="self-start bg-white border border-gray-200 p-3 rounded-2xl rounded-tl-sm shadow-sm flex items-center gap-1.5" data-id="element-1267">
                  <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.3s]" data-id="element-1268"></div>
                  <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.15s]" data-id="element-1269"></div>
                  <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" data-id="element-1270"></div>
                </div>}
              <div ref={messagesEndRef} data-id="element-1271" />
            </div>

            {/* Suggested Prompts (only show if no user messages yet) */}
            {messages.length === 1 && <div className="px-4 pb-2 bg-gray-50 shrink-0" data-id="element-1272">
                <div className="flex flex-wrap gap-2" data-id="element-1273">
                  {TAB_CONFIGS[tabContext].prompts.map((prompt, idx) => <button key={idx} onClick={() => handleSend(prompt)} className="text-xs bg-white border border-indigo-100 text-indigo-700 px-3 py-1.5 rounded-full hover:bg-indigo-50 hover:border-indigo-300 transition-colors text-left" data-id="element-1274">
                      {prompt}
                    </button>)}
                </div>
              </div>}

            {/* Input Area */}
            <div className="p-3 bg-white border-t border-gray-200 shrink-0" data-id="element-1275">
              <div className="relative flex items-center" data-id="element-1276">
                <input type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyDown={e => {
              if (e.key === 'Enter') handleSend(inputValue);
            }} placeholder="Ask Bilti..." className="w-full pl-4 pr-10 py-2.5 bg-gray-100 border-transparent focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 rounded-xl text-sm transition-all" data-id="element-1277" />
                <button onClick={() => handleSend(inputValue)} disabled={!inputValue.trim() || isTyping} className="absolute right-2 p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg disabled:opacity-50 disabled:hover:bg-transparent transition-colors" data-id="element-1278">
                  <Send size={16} data-id="element-1279" />
                </button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* FAB */}
      <motion.button whileHover={{
      scale: 1.05
    }} whileTap={{
      scale: 0.95
    }} onClick={() => setIsOpen(!isOpen)} className={`relative flex items-center gap-2 px-4 py-3 rounded-full shadow-lg transition-colors ${isOpen ? 'bg-slate-800 text-white hover:bg-slate-700' : 'bg-amber-400 text-slate-900 hover:bg-amber-300'}`} data-id="element-1280">
        {!isOpen && <span className="absolute inset-0 rounded-full bg-amber-400 animate-ping opacity-20" data-id="element-1281"></span>}
        {isOpen ? <X size={20} data-id="element-1282" /> : <>
            <Sparkles size={20} data-id="element-1283" />
            <span className="font-semibold text-sm" data-id="element-1284">Ask Bilti</span>
          </>}
      </motion.button>
    </div>;
}